import React, { useEffect } from 'react';
import { HiXMark } from 'react-icons/hi2';
import './LegalModal.css';

const PRIVACY = {
  title: 'Privacy Policy',
  updated: 'Last updated: April 2026',
  body: [
    {
      type: 'p',
      text: `StoreShot.io ("we", "our", or "us"), operated by Kairos, is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website or use our services.`,
    },
    { type: 'h', text: 'Information We Collect' },
    {
      type: 'p',
      text: 'We may collect information about you in various ways:',
    },
    {
      type: 'ul',
      items: [
        'Personal Data: Name, email address, and project details you provide through our contact form or during checkout.',
        'Payment Data: Payment information is processed securely by Stripe. We do not store credit card details on our servers.',
        'Usage Data: Information about how you interact with our website, including pages visited, time spent, and referring URLs.',
        'Device Data: Browser type, operating system, IP address, and device identifiers.',
      ],
    },
    { type: 'h', text: 'How We Use Your Information' },
    {
      type: 'ul',
      items: [
        'Deliver the screenshot design services you have purchased',
        'Respond to your inquiries and provide customer support',
        'Send you project updates and delivery files',
        'Improve our website and services',
        'Comply with legal obligations',
      ],
    },
    { type: 'h', text: 'Cookies and Tracking' },
    {
      type: 'p',
      text: 'We use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or indicate when a cookie is being sent.',
    },
    { type: 'h', text: 'Third-Party Services' },
    {
      type: 'ul',
      items: [
        'Stripe — for secure payment processing',
        'Supabase — for storing project assets and screenshots',
        'Formspree — for contact form submissions',
        'Google Analytics — for website analytics (if enabled)',
      ],
    },
    { type: 'h', text: 'Data Security' },
    {
      type: 'p',
      text: 'We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.',
    },
    { type: 'h', text: 'Your Rights' },
    {
      type: 'p',
      text: 'Depending on your location, you may have the following rights:',
    },
    {
      type: 'ul',
      items: [
        'Access to your personal data',
        'Correction of inaccurate data',
        'Deletion of your data',
        'Objection to data processing',
        'Data portability',
      ],
    },
    { type: 'h', text: 'Contact Us' },
    {
      type: 'p',
      text: 'If you have any questions about this Privacy Policy, please contact us:',
    },
    {
      type: 'p',
      text: 'StoreShot.io (Kairos)\nParis, France\nWhatsApp: +33 6 14 39 56 76\nEmail: hello@storeshot.io',
    },
  ],
};

const TERMS = {
  title: 'Terms of Service',
  updated: 'Last updated: April 2026',
  body: [
    {
      type: 'p',
      text: `Welcome to StoreShot.io. These Terms of Service ("Terms") govern your use of our website and services. By accessing or using our services, you agree to be bound by these Terms.`,
    },
    { type: 'h', text: 'Services' },
    {
      type: 'p',
      text: 'StoreShot.io is a design service specializing in high-conversion App Store and Google Play screenshots for iOS, iPad, and Android applications. Our services include custom screenshot design, App Store metadata copywriting, and brand assets for app store listings.',
    },
    { type: 'h', text: 'Orders & Payment' },
    {
      type: 'ul',
      items: [
        'All payments are processed securely through Stripe',
        'Pricing is one-time per project, as displayed on the pricing page',
        'Once an order is placed, you will receive a confirmation email and onboarding instructions',
        'Project work begins after we receive your app details and brand materials',
        'Delivery times begin from the moment we have everything needed to start',
      ],
    },
    { type: 'h', text: 'Revisions & Delivery' },
    {
      type: 'ul',
      items: [
        'The number of revision rounds is specified per plan',
        'Revisions must be requested within 14 days of initial delivery',
        'Final files are delivered as PNG/JPG and source files (Figma)',
        'Delivery times are estimates and may vary based on revision rounds',
      ],
    },
    { type: 'h', text: 'Refund Policy' },
    {
      type: 'p',
      text: 'If you are not satisfied with the initial designs, you may request a full refund within 7 days of delivery, provided no revisions have been requested. Once revisions begin, the project is considered active and refunds are no longer available.',
    },
    { type: 'h', text: 'Intellectual Property' },
    {
      type: 'p',
      text: 'Upon full payment, you own the final delivered screenshots and may use them on the App Store, Google Play, your website, marketing materials, and social media. StoreShot retains the right to display the work in our portfolio unless you request otherwise in writing.',
    },
    { type: 'h', text: 'Website Use' },
    {
      type: 'p',
      text: 'When using our website, you agree not to:',
    },
    {
      type: 'ul',
      items: [
        'Use the website for any unlawful purpose',
        'Attempt to gain unauthorized access to any part of the website',
        'Interfere with or disrupt the website or servers',
        'Transmit any viruses, malware, or harmful code',
      ],
    },
    { type: 'h', text: 'Limitation of Liability' },
    {
      type: 'p',
      text: 'StoreShot shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of our services. Our total liability shall not exceed the amount paid by you for the specific service giving rise to the claim. We do not guarantee specific download or conversion results — past results are illustrative and depend on many factors outside our control.',
    },
    { type: 'h', text: 'Disclaimer' },
    {
      type: 'p',
      text: 'Our website and services are provided "as is" without warranties of any kind, either express or implied.',
    },
    { type: 'h', text: 'Governing Law' },
    {
      type: 'p',
      text: 'These Terms shall be governed by and construed in accordance with the laws of France. Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the courts of Paris, France.',
    },
    { type: 'h', text: 'Changes to Terms' },
    {
      type: 'p',
      text: 'We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website.',
    },
    { type: 'h', text: 'Contact Us' },
    {
      type: 'p',
      text: 'StoreShot.io (Kairos)\nParis, France\nWhatsApp: +33 6 14 39 56 76\nEmail: hello@storeshot.io',
    },
  ],
};

const CONTENT = { privacy: PRIVACY, terms: TERMS };

export default function LegalModal({ type, onClose }) {
  useEffect(() => {
    const handleEsc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', handleEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  if (!type || !CONTENT[type]) return null;
  const doc = CONTENT[type];

  return (
    <div className="legal-modal" onClick={onClose}>
      <div className="legal-modal__content" onClick={(e) => e.stopPropagation()}>
        <button className="legal-modal__close" onClick={onClose} aria-label="Close">
          <HiXMark />
        </button>

        <div className="legal-modal__body">
          <h1 className="legal-modal__title">{doc.title}</h1>
          <p className="legal-modal__updated">{doc.updated}</p>

          {doc.body.map((block, i) => {
            if (block.type === 'h') return <h2 key={i}>{block.text}</h2>;
            if (block.type === 'p') return <p key={i}>{block.text}</p>;
            if (block.type === 'ul') return (
              <ul key={i}>
                {block.items.map((item, j) => <li key={j}>{item}</li>)}
              </ul>
            );
            return null;
          })}
        </div>
      </div>
    </div>
  );
}
